import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import CreditInvoiceModal from "../../components/invoices/CreditInvoiceModal";
import InvoiceItemModal from "../../components/invoices/InvoiceItemModal";
import InvoiceItemsTable from "../../components/invoices/InvoiceItemsTable";
import Card from "../../components/shared/Card/Card";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import { DateTimeInput } from "../../components/shared/Inputs/DateTimeInput";
import { TextInput } from "../../components/shared/Inputs/TextInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import StatusPill, { StatusPillColour } from "../../components/shared/StatusPill";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useInvoiceMutations from "../../data/useInvoiceMutations";
import useInvoiceSingle from "../../data/useInvoiceSingle";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IAccount } from "../../models/account/IAccount";
import { InvoiceStatus } from "../../models/invoice/IInvoice";
import { dateTimeToFormValue } from "../../utils/dateTimeToFormValue";
import BasketInvoiceInputs from "../Basket/BasketInvoiceInputs";
import useNotification from "../../components/notifications/useNotifications";

export default function ViewInvoice() {
  const { t } = useTranslation("accounts");
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { setInfo } = usePageNameContext();
  const { invoice: invoiceQuery } = useInvoiceSingle(invoiceId);
  const invoice = invoiceQuery.data?.data ?? null;

  const [selectedAccount, setSelectedAccount] = useState<IAccount | null>(null);
  const [invoiceItemOpen, setInvoiceItemOpen] = useState(false);
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [closeOnSave, setCloseOnSave] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const { updateInvoice, downloadInvoice } = useInvoiceMutations();
  const { saveHandlers } = useDefaultCRUDHandlers(t("Invoice"));
  const { addNotification } = useNotification();
  const { register, setValue, getValues, watch, reset } = useForm({
    defaultValues: {
      paymentMethod: 1,
      accountId: "0",
      shippingPolicyId: "0",
      notes: "",
      postcode: "",

      line1: "",
      line2: "",
      line3: "",
      line4: "",
      locality: "",
      town: "",
      county: "",

      status: InvoiceStatus.Draft,

      invoiceDate: "",
      dueDate: "",
      reference: "",
      amountPaid: 0,
      subTotal: 0,
      invoiceTotal: 0,

      itemTotal: 0
    }
  });

  const itemTotal = watch("itemTotal");
  const invoiceTotal = watch("invoiceTotal");

  useEffect(() => {
    setInfo({
      name: invoice ? t("Invoice #{{number}}", { number: invoice.invoiceNumber }) : t("Invoice"),
      desc: t("View and manage this invoice here"),
    });
  }, [setInfo, invoice, t]);

  useEffect(() => {
    if (!invoice) return;

    setSelectedAccount(invoice.account || null);

    reset({
      paymentMethod: invoice.paymentMethod,
      accountId: invoice.accountId || "",
      shippingPolicyId: invoice?.basket?.shippingPolicyId || "0",
      notes: invoice.notes || "",

      postcode: invoice?.postcode || "",
      line1: invoice.addressLine1 || "",
      line2: invoice.addressLine2 || "",
      line3: invoice.addressLine3 || "",
      line4: invoice.addressLine4 || "",
      locality: "",
      town: invoice.town || "",
      county: invoice.county || "",

      invoiceDate: dateTimeToFormValue(invoice.invoiceDate),
      dueDate: dateTimeToFormValue(invoice.dueDate),
      reference: invoice.reference,
      amountPaid: invoice.amountPaid,
      subTotal: invoice.subTotal,
      invoiceTotal: invoice.invoiceTotal,
    });
  }, [invoice, reset]);

  useEffect(() => {
    if (closeOnSave) return;
    updateInvoiceValues();
  }, [closeOnSave]);

  const setTotalToItemTotal = () => {
    setValue("invoiceTotal", itemTotal);
    setCloseOnSave(false);
  }

  const updateInvoiceValues = () => {
    const formData = getValues();

    updateInvoice.mutate({
      id: invoice?.id || "",
      payload: {
        ...formData,
        accountId: getValues("accountId") || invoice?.accountId
      }
    }, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        if (closeOnSave) navigate(-1);
        else setCloseOnSave(true);
      }
    });
  }

  const handleDownloadInvoice = () => {
    if (!invoice?.id || downloading) return;
    setDownloading(true);
    downloadInvoice(invoice.id)
      .catch(() =>
        addNotification({
          variant: "error",
          primaryText: t("Failed to download invoice"),
        })
      )
      .finally(() => setDownloading(false));
  }

  const isEmpty = useMemo(() => {
    return invoice?.items && invoice.items.length === 0;
  }, [invoice]);

  if (invoiceQuery.isLoading) {
    return (
      <div className="min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
        <LoadingWheel />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow flex items-center justify-center">
        <p className="text-red-600 font-bold text-xl">{t("Invoice not found")}</p>
      </div>
    );
  }

  return (
    <>
      <CreditInvoiceModal open={creditModalOpen} setOpen={setCreditModalOpen} invoice={invoice} />
      <InvoiceItemModal open={invoiceItemOpen} setOpen={setInvoiceItemOpen} invoice={invoice} />

      <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <div className="flex flex-col gap-6">
          <Card title={t("Invoice Details")} bodyClassName="px-4 py-5 sm:p-6">
            {(invoice?.isGlobalSale != null || invoice?.items?.some((i) => i.isMarginScheme)) && (
              <div className="mb-4 flex items-center gap-2 flex-wrap">
                {invoice.isGlobalSale
                  ? <StatusPill text={t("GLOBAL")} colour={StatusPillColour.Blue} />
                  : invoice.items?.some((i) => i.isMarginScheme)
                    ? <StatusPill text={t("MARGIN")} colour={StatusPillColour.Orange} />
                    : <StatusPill text={t("VAT")} colour={StatusPillColour.Green} />
                }
                {invoice.isGlobalSale && (
                  <span className="text-xs text-gray-500">{t("eBay Global Shipping Programme — no UK VAT")}</span>
                )}
                {!invoice.isGlobalSale && invoice.items?.some((i) => i.isMarginScheme) && (
                  <span className="text-xs text-gray-500">{t("VAT Global Accounting — VAT on margin, not shown separately")}</span>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DateTimeInput
                label={t("Invoice Date")}
                register={register}
                registerName="invoiceDate"
              />
              <DateTimeInput
                label={t("Due Date")}
                register={register}
                registerName="dueDate"
              />
              <TextInput
                label={t("Reference")}
                register={register}
                registerName="reference"
                placeholder={t("Invoice reference")}
                disabled={true}
              />
              <TextInput
                label={t("Amount Paid")}
                register={register}
                registerName="amountPaid"
                type="number"
                placeholder={t("Enter amount paid")}
              />
              <TextInput
                label={t("Sub Total")}
                register={register}
                registerName="subTotal"
                type="number"
                placeholder={t("Enter total amount")}
              />
              <TextInput
                label={t("Invoice Total")}
                register={register}
                registerName="invoiceTotal"
                type="number"
                placeholder={t("Enter invoice total")}
              />
            </div>
            <div className="mt-4 flex justify-end">
              <ButtonInput
                isSubmit={false}
                onClick={setTotalToItemTotal}
                label={t("Set Total to Item Total")}
                classes=""
                disabled={itemTotal === invoiceTotal}
              />
            </div>
          </Card>

          <Card title={t("Invoice Items")} bodyClassName="px-4 py-5 sm:p-6">
            <>
              {isEmpty ? (
                <p className="mt-8 text-center text-xl text-gray-700">{t("No items attached to this invoice")}</p>
              ) :
                <InvoiceItemsTable
                  data={invoice?.items ?? []}
                  register={register}
                  setValue={setValue}
                  getValues={getValues}
                />
              }
              <div className="mt-4 flex justify-end">
                <ButtonInput
                  isSubmit={false}
                  onClick={() => setInvoiceItemOpen(true)}
                  label={t("Add Item")}
                  classes=""
                />
              </div>
            </>
          </Card>
        </div>

        <Card title={t("Account Details")} bodyClassName="px-4 py-5 sm:p-6">
          <div className="flex flex-col w-full gap-4">
            <BasketInvoiceInputs
              register={register}
              setValue={setValue}
              watch={watch}
              setSelectedAccount={setSelectedAccount}
              selectedAccount={selectedAccount}
              disabled={invoice?.status == InvoiceStatus.Paid}
              showTaxOption={false}
            />

            <div className="flex justify-between items-center">
              <ButtonInput
                isSubmit={false}
                onClick={() => navigate(-1)}
                label={t("Back")}
                classes="!bg-gray-200 hover:!bg-gray-300 !text-gray-800"
              />

              <div className="flex gap-4">
                <ButtonInput
                  isSubmit={false}
                  onClick={handleDownloadInvoice}
                  label={t("Download Invoice")}
                  isLoading={downloading}
                  classes=""
                />
                {invoice?.status != InvoiceStatus.Credit && (
                  <ButtonInput
                    isSubmit={false}
                    onClick={() => setCreditModalOpen(true)}
                    label={t("Credit Invoice")}
                    classes="bg-red-600 hover:bg-red-700 text-white"
                  />
                )}

                <ButtonInput
                  isSubmit={false}
                  onClick={updateInvoiceValues}
                  label={t("Update Invoice")}
                  classes=""
                />
              </div>
            </div>
          </div>
        </Card>
      </div>
    </>
  );
}
